import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost/papad/public';

function buildErrorMessage(error) {
  if (error.code === 'ERR_CANCELED') {
    return 'The request was cancelled before it completed.';
  }

  if (error.code === 'ECONNABORTED') {
    return 'The server took too long to respond. Please try again.';
  }

  if (!error.response) {
    return 'Unable to reach the server. Please check your connection and try again.';
  }

  const { status, data } = error.response;

  if (typeof data?.message === 'string' && data.message.trim()) {
    return data.message;
  }

  if (status === 400) {
    return 'The request could not be processed. Please review your input.';
  }

  if (status === 401) {
    return 'Your session is not authorized. Please sign in again.';
  }

  if (status === 403) {
    return 'You do not have permission to perform this action.';
  }

  if (status === 404) {
    return 'The requested API endpoint was not found.';
  }

  if (status === 422) {
    return 'Please correct the highlighted fields and try again.';
  }

  if (status >= 500) {
    return 'The server ran into a problem. Please try again shortly.';
  }

  return 'Something went wrong while processing your request.';
}

function buildValidationErrors(data) {
  const errors = data?.errors || data?.validation_errors || {};

  return Object.entries(errors).reduce((accumulator, [field, value]) => {
    if (Array.isArray(value)) {
      accumulator[field] = value[0];
      return accumulator;
    }

    if (typeof value === 'string') {
      accumulator[field] = value;
    }

    return accumulator;
  }, {});
}

export function normalizeApiError(error) {
  const status = error.response?.status ?? null;
  const data = error.response?.data ?? null;

  return {
    status,
    code: error.code || null,
    message: buildErrorMessage(error),
    validationErrors: buildValidationErrors(data),
    raw: data,
    isCanceled: error.code === 'ERR_CANCELED',
    isTimeout: error.code === 'ECONNABORTED',
    isNetworkError: !error.response && error.code !== 'ERR_CANCELED',
    isValidationError: status === 422,
    isAuthError: status === 401 || status === 403,
    isServerError: typeof status === 'number' && status >= 500,
  };
}

function createApiClient(tokenStorageKey) {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 15000,
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });

  client.interceptors.request.use(
    (config) => {
      const nextConfig = { ...config };
      const token = localStorage.getItem(tokenStorageKey);

      if (token) {
        nextConfig.headers.Authorization = `Bearer ${token}`;
      }

      if (!(nextConfig.data instanceof FormData)) {
        nextConfig.headers['Content-Type'] = 'application/json';
      }

      return nextConfig;
    },
    (error) => Promise.reject(normalizeApiError(error))
  );

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      const normalizedError = normalizeApiError(error);

      if (normalizedError.status === 401) {
        localStorage.removeItem(tokenStorageKey);
      }

      return Promise.reject(normalizedError);
    }
  );

  return client;
}

export const customerApiClient = createApiClient('customer_token');
export const adminApiClient = createApiClient('admin_token');

export default customerApiClient;
